// src/components/dashboard/TrendingPlayersSection.js
import React, { useState, useEffect } from 'react';
import { Flame, Snowflake } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { mlbAPI } from '../../services/apiService';
import useDefaultLeague from '../../hooks/useDefaultLeague';
import {
  DynastyTable,
  createHotHittersColumns,
  createColdHittersColumns,
  createHotPitchersColumns,
  createColdPitchersColumns,
  createWaiverAddsColumns,
  createWaiverDropsColumns
} from '../../services/tableService';

const TrendingPlayersSection = ({ leagues = [], isAuthenticated = false }) => {
  const [activeTab, setActiveTab] = useState('hitting');
  const [hotHitters, setHotHitters] = useState([]);
  const [coldHitters, setColdHitters] = useState([]);
  const [hotPitchers, setHotPitchers] = useState([]);
  const [coldPitchers, setColdPitchers] = useState([]);
  const [waiverAdds, setWaiverAdds] = useState([]);
  const [waiverDrops, setWaiverDrops] = useState([]);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [error, setError] = useState(null);

  // Server-side default league preference — single source of truth
  const { defaultLeagueId, setDefaultLeagueId, loading: leagueLoading } = useDefaultLeague(leagues, isAuthenticated);

  const handleLeagueChange = (newLeagueId) => {
    setDefaultLeagueId(newLeagueId);
  };

  // Reload when default league changes
  useEffect(() => {
    if (!leagueLoading) {
      loadTrendingPlayers();
    }
  }, [defaultLeagueId, leagueLoading]);

  const loadTrendingPlayers = async () => {
    try {
      setLoadingTrends(true);
      setError(null);

      const leagueId = isAuthenticated ? defaultLeagueId : null;

      const hittersResponse = await mlbAPI.getTrendingPlayers('', leagueId);

      if (hittersResponse && hittersResponse.success) {
        setHotHitters(hittersResponse.hot_players || []);
        setColdHitters(hittersResponse.cold_players || []);
        setWaiverAdds(hittersResponse.waiver_adds || []);
        setWaiverDrops(hittersResponse.waiver_drops || []);
      }

      const pitchersResponse = await mlbAPI.getTrendingPlayers('?player_type=pitching', leagueId);

      if (pitchersResponse && pitchersResponse.success) {
        setHotPitchers(pitchersResponse.hot_players || []);
        setColdPitchers(pitchersResponse.cold_players || []);
      }

    } catch (err) {
      console.error('Error loading trending players:', err);
      setError(err.message);
    } finally {
      setLoadingTrends(false);
    }
  };

  if (loadingTrends) {
    return (
      <div className="space-y-6">
        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <div className="flex justify-center items-center py-8">
            <div className="flex items-center space-x-3">
              <div
                className={`w-6 h-6 border-2 border-t-transparent rounded-full animate-spin ${dynastyTheme.classes.border.primaryBright}`}
              />
              <span className={dynastyTheme.classes.text.white}>Loading player trends...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className={`${dynastyTheme.components.card.base} p-4 border-l-4 border-yellow-500`}>
          <div className="flex items-center space-x-2">
            <span className="text-yellow-400">&#9888;</span>
            <span className={dynastyTheme.classes.text.white}>
              API Error: {error}
            </span>
            <button
              onClick={loadTrendingPlayers}
              className={`text-sm ${dynastyTheme.classes.text.primary} hover:text-yellow-300 underline ml-2`}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Controls row: Hitters/Pitchers tabs + League selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('hitting')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              activeTab === 'hitting'
                ? `${dynastyTheme.components.button.primary} text-gray-900`
                : `bg-gray-700 ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-600`
            }`}
          >
            Hitters
          </button>
          <button
            onClick={() => setActiveTab('pitching')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              activeTab === 'pitching'
                ? `${dynastyTheme.components.button.primary} text-gray-900`
                : `bg-gray-700 ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-600`
            }`}
          >
            Pitchers
          </button>
        </div>

        {/* League selector — only for authenticated users with leagues */}
        {isAuthenticated && leagues.length > 0 && (
          <div className="flex items-center gap-2">
            <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>League:</span>
            <select
              value={defaultLeagueId || ''}
              onChange={(e) => handleLeagueChange(e.target.value)}
              className={`text-xs px-2 py-1 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral}`}
            >
              {leagues.map(l => (
                <option key={l.league_id} value={l.league_id}>{l.league_name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Hot Players Table */}
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <div className="flex items-center space-x-2 mb-3">
          <Flame className="w-5 h-5 text-orange-500" />
          <h3 className={`font-semibold ${dynastyTheme.classes.text.white}`}>
            Hot {activeTab === 'hitting' ? 'Hitters' : 'Pitchers'} (Last 7 Days)
          </h3>
        </div>
        {activeTab === 'hitting' ? (
          hotHitters && hotHitters.length > 0 ? (
            <DynastyTable
              tableType="flat-data"
              data={hotHitters}
              columns={createHotHittersColumns()}
              maxHeight="300px"
              enableHorizontalScroll={true}
              enableVerticalScroll={true}
              stickyHeader={false}
            />
          ) : (
            <div className="text-center py-4 text-gray-400">No hot hitters found</div>
          )
        ) : (
          hotPitchers && hotPitchers.length > 0 ? (
            <DynastyTable
              tableType="flat-data"
              data={hotPitchers}
              columns={createHotPitchersColumns()}
              maxHeight="300px"
              enableHorizontalScroll={true}
              enableVerticalScroll={true}
              stickyHeader={false}
            />
          ) : (
            <div className="text-center py-4 text-gray-400">No hot pitchers found</div>
          )
        )}
      </div>

      {/* Cold Players Table */}
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <div className="flex items-center space-x-2 mb-3">
          <Snowflake className="w-5 h-5 text-blue-400" />
          <h3 className={`font-semibold ${dynastyTheme.classes.text.white}`}>
            Cold {activeTab === 'hitting' ? 'Hitters' : 'Pitchers'}
          </h3>
        </div>
        {activeTab === 'hitting' ? (
          coldHitters && coldHitters.length > 0 ? (
            <DynastyTable
              tableType="flat-data"
              data={coldHitters}
              columns={createColdHittersColumns()}
              maxHeight="250px"
              enableHorizontalScroll={true}
              enableVerticalScroll={true}
              stickyHeader={false}
            />
          ) : (
            <div className="text-center py-4 text-gray-400">No cold hitters found</div>
          )
        ) : (
          coldPitchers && coldPitchers.length > 0 ? (
            <DynastyTable
              tableType="flat-data"
              data={coldPitchers}
              columns={createColdPitchersColumns()}
              maxHeight="250px"
              enableHorizontalScroll={true}
              enableVerticalScroll={true}
              stickyHeader={false}
            />
          ) : (
            <div className="text-center py-4 text-gray-400">No cold pitchers found</div>
          )
        )}
      </div>

      {/* Waiver Wire Trends */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <div className="flex items-center space-x-2 mb-3">
            <h3 className={`font-semibold ${dynastyTheme.classes.text.white}`}>Most Added (48h)</h3>
          </div>
          {waiverAdds && waiverAdds.length > 0 ? (
            <DynastyTable
              tableType="flat-data"
              data={waiverAdds}
              columns={createWaiverAddsColumns()}
              maxHeight="200px"
              enableHorizontalScroll={true}
              enableVerticalScroll={true}
              stickyHeader={false}
            />
          ) : (
            <div className="text-center py-4 text-gray-400">No recent adds</div>
          )}
        </div>

        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <div className="flex items-center space-x-2 mb-3">
            <h3 className={`font-semibold ${dynastyTheme.classes.text.white}`}>Most Dropped (48h)</h3>
          </div>
          {waiverDrops && waiverDrops.length > 0 ? (
            <DynastyTable
              tableType="flat-data"
              data={waiverDrops}
              columns={createWaiverDropsColumns()}
              maxHeight="200px"
              enableHorizontalScroll={true}
              enableVerticalScroll={true}
              stickyHeader={false}
            />
          ) : (
            <div className="text-center py-4 text-gray-400">No recent drops</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendingPlayersSection;
